import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { MobileMediaPreview } from '../../../CoCreatePanel/MediaPreview'
import { buildMediaSlots } from '../../../../media'
import { HeadingIcon } from 'images'
import MediaListContainer from '../../MediaListContainer'
import PreviewMediaCard from 'components/Shared/MediaCard/PreviewMediaCard'
import EmptyCard from '../EmptyCard'
import ErrorMessage from 'components/Shared/ErrorMessage'
import { useTranslation } from 'react-i18next'
import { LibraryPickerModal } from 'components/Library/element/LibraryPickerModal'
import { useMediaGroupSlotActions } from './useMediaGroupSlotActions'

const getFilledSlotMenuItems = ({
  image,
  t,
  isCampaignRunning,
  readonly,
  onLibraryReplace,
  onDeviceReplace,
  onRemove,
}) => {
  if (!image || readonly) {
    return []
  }

  const items = [
    {
      key: 'library',
      label: t('Recommendations.campaign.setup.media.dropdown.replace_from_library'),
      onClick: onLibraryReplace,
    },
    {
      key: 'device',
      label: t('Recommendations.campaign.setup.media.dropdown.replace_from_device'),
      onClick: onDeviceReplace,
    }
  ]

  if (image.is_generated && onRemove && !readonly && !isCampaignRunning) {
    items.push({
      key: 'remove',
      label: t('Recommendations.campaign.setup.media.dropdown.remove'),
      onClick: onRemove,
    })
  }

  return items
}

export default function MediaGroupPreviewModal({
  group,
  originalMedia,
  isCampaignRunning = false,
  readonly = false,
  onAddToSlot,
  onAddLibraryToSlot,
  onReplaceLibraryToSlot,
  onReplaceInSlot,
  onRemoveFromSlot,
}) {
  const { t } = useTranslation()
  const [mobilePreviewFile, setMobilePreviewFile] = useState(null)
  const images = group?.images ?? []
  const mediaSlots = buildMediaSlots(images)
  const {
    slotError,
    isLibraryPickerOpen,
    fileInputRef,
    handleFileChange,
    openFilePicker,
    openLibraryAdd,
    openLibraryReplace,
    closeLibraryPicker,
    handleLibraryApply,
    handleRemoveSlot,
  } = useMediaGroupSlotActions({
    group,
    images,
    originalMedia,
    canManageMedia: !readonly,
    onAddToSlot,
    onAddLibraryToSlot,
    onReplaceLibraryToSlot,
    onReplaceInSlot,
    onRemoveFromSlot,
  })

  return (
    <div className="media-group-preview-modal">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg, image/png, video/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <div className="content">
        {group?.headline && (
          <p className='headline'>
            <HeadingIcon width={14} height={14} />
            <span>{group.headline}</span>
          </p>
        )}

        {slotError && (
          <ErrorMessage variant="light" classNames="mt-0">{slotError}</ErrorMessage>
        )}

        <MediaListContainer>
          {mediaSlots.map(({ ratio, image }) => {
            const canRemove = !readonly && !isCampaignRunning
            const onRemove = canRemove
              ? () => handleRemoveSlot(image.id)
              : undefined

            const actionMenuItems = image
              ? getFilledSlotMenuItems({
                image,
                t,
                isCampaignRunning,
                readonly,
                onLibraryReplace: () => openLibraryReplace(ratio, image.id),
                onDeviceReplace: () => openFilePicker({ type: 'replace', fileId: image.id, ratio }),
                onRemove,
              })
              : []

            const canAdd = !readonly

            return image ? (
              <PreviewMediaCard
                key={image.id}
                item={image}
                selectiveMode={false}
                onRemove={onRemove}
                actionMenuItems={actionMenuItems}
                onClick={setMobilePreviewFile}
              />
            ) : (
              <EmptyCard
                key={ratio}
                aspectRatio={ratio}
                disabled={!canAdd}
                onDeviceUpload={
                  canAdd
                    ? () => openFilePicker({ type: 'add', ratio })
                    : undefined
                }
                onLibraryUpload={canAdd ? () => openLibraryAdd(ratio) : undefined}
              />
            )
          })}
        </MediaListContainer>
      </div>

      <MobileMediaPreview file={mobilePreviewFile} onClose={() => setMobilePreviewFile(null)} />

      <LibraryPickerModal
        isOpen={isLibraryPickerOpen}
        onClose={closeLibraryPicker}
        onApply={handleLibraryApply}
        selectionMode="single"
      />
    </div>
  )
}

MediaGroupPreviewModal.propTypes = {
  group: PropTypes.shape({
    subGroupId: PropTypes.string,
    headline: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.object),
    originalId: PropTypes.string,
  }).isRequired,
  originalMedia: PropTypes.object,
  isCampaignRunning: PropTypes.bool,
  readonly: PropTypes.bool,
  onAddToSlot: PropTypes.func,
  onAddLibraryToSlot: PropTypes.func,
  onReplaceLibraryToSlot: PropTypes.func,
  onReplaceInSlot: PropTypes.func,
  onRemoveFromSlot: PropTypes.func,
}
